import React from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { uiActions } from '../store/ui/uiSlice';
import { openChatUI, refreshThreads } from '../store/chat/chatSlice';
import { fastapi } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxOpen,
  faShoppingCart,
  faCalendarCheck,
  faTruck,
  faFlask,
  faRobot,
  faDiagramProject,
  faTags,
  faXmark,
  faBell,
  faAnglesLeft,
  faChevronRight,
  faFileInvoice, faSignOutAlt, faChevronDown,
  faChartPie, faUsers, faIndent,
  faCog,
  faMoon,
  faSun,
  faClinicMedical,
  faUserMd,
  faUser,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { ES as FlagES, GB as FlagGB } from 'country-flag-icons/react/3x2';
import { SlidingPills } from './ui/SlidingPills';
import { GhostSwitch } from './ui/GhostSwitch';
import { getSecondsLeft, isExpired, formatPrettyLeft } from '../utils/jwt';
import { logoutThunk } from '../store/auth/authSlice';
import { useTranslation } from 'react-i18next';
import { useGetActiveByChannelQuery } from '../store/bots/botsApi';
import { summarizeChatNotifications } from '../utils/chatNotifications';
import SettingsNavigation from './settings/SettingsNavigation';

type MenuItem = {
  to: string;
  label: string;
  icon: any;
  subItems?: { to: string; label: string; icon: any }[];
  featureKey?: string;
};

const MENU: MenuItem[] = [
  { to: '/products', label: 'Productos', icon: faBoxOpen, featureKey: 'products' },
  { to: '/orders', label: 'Pedidos', icon: faShoppingCart, featureKey: 'orders' },
  { to: '/delivery-settings', label: 'Delivery Settings', icon: faTruck, featureKey: 'delivery_settings' },
  { to: '/test-api', label: 'Test API', icon: faFlask, featureKey: 'test_api' },
  { to: '/bots-config', label: 'Bots Config', icon: faRobot, featureKey: 'chatbots' },
  { to: '/workflows', label: 'Workflows', icon: faDiagramProject },
  { to: '/post-bot', label: 'Bot de publicaciones', icon: faRobot, featureKey: 'chatbots' },
  { to: '/stats', label: 'Estadísticas', icon: faChartPie, featureKey: 'chatbots' },
  { to: '/tags-config', label: 'Tags', icon: faTags, featureKey: 'tags' },
  {
    to: '/clinics',
    label: 'Clínica',
    icon: faClinicMedical,
    featureKey: 'clinics',
    subItems: [
      { to: '/clinics/dashboard', label: 'Dashboard', icon: faClinicMedical },
      { to: '/clinics/doctors', label: 'Doctores', icon: faUserMd },
      { to: '/clinics/patients', label: 'Pacientes', icon: faUsers },
      { to: '/clinics/appointments', label: 'Citas', icon: faCalendarCheck },
    ]
  },
  { to: '/invoices', label: 'Facturación', icon: faFileInvoice, featureKey: 'invoices' },
];

const DesktopSidebar: React.FC<{
  enabledFeatures: string[] | null;
  channelDefaultBotId: number | null | undefined;
}> = ({ enabledFeatures, channelDefaultBotId }) => {
  const isCollapsed = useAppSelector((s) => s.ui.isCollapsed);
  const theme = useAppSelector((s) => s.ui.theme);
  const width = isCollapsed ? 72 : 240;
  const { t, i18n } = useTranslation();
  const [hoveredItemId, setHoveredItemId] = React.useState<string | null>(null);
  const [openCollapsedMenu, setOpenCollapsedMenu] = React.useState<string | null>(null);
  const [flyoutItem, setFlyoutItem] = React.useState<MenuItem | null>(null);
  const [flyoutPos, setFlyoutPos] = React.useState<{ top: number; left: number } | null>(null);
  const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showSettings, setShowSettings] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState<Record<string, boolean>>({ '/clinics': false, '/bookings': false });

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const scheduleCloseFlyout = () => {
    clearHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      setFlyoutItem(null);
      setFlyoutPos(null);
      setHoveredItemId(null);
    }, 200);
  };

  const handleMenuMouseEnter = (item: MenuItem, event: React.MouseEvent<HTMLElement>) => {
    clearHoverTimeout();
    setHoveredItemId(item.to);
    if (isCollapsed && item.subItems && item.subItems.length > 0) {
      const rect = event.currentTarget.getBoundingClientRect();
      setFlyoutPos({ top: rect.top, left: rect.right + 4 });
      setFlyoutItem(item);
    }
  };

  const handleMenuMouseLeave = () => {
    scheduleCloseFlyout();
  };

  const handlePopoverMouseEnter = () => {
    clearHoverTimeout();
  };

  const handlePopoverMouseLeave = () => {
    scheduleCloseFlyout();
  };
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const unreadCount = useAppSelector((s) => s.chat.unreadCount);
  const threadsLoading = useAppSelector((s) => s.chat.threadsLoading);
  const connectionStatus = useAppSelector((s) => s.chat.connectionStatus);
  const threads = useAppSelector((s) => s.chat.threads);

  React.useEffect(() => {
    if (!isCollapsed) {
      setOpenCollapsedMenu(null);
    }
  }, [isCollapsed]);

  React.useEffect(() => {
    if (threads && Array.isArray(threads) && threads.length > 0) {
      const isMissingMetadata = threads.some((t: any) => t.bot_enabled === undefined || t.lastSenderKind === undefined);
      if (isMissingMetadata && user?.comercioKey) {
        dispatch(refreshThreads());
      }
    }
  }, [threads, user?.comercioKey, dispatch]);

  // Use the same explicit notification-state rules as ChatSidebar.
  const notificationSummary = React.useMemo(
    () => summarizeChatNotifications(threads, unreadCount, channelDefaultBotId),
    [threads, unreadCount, channelDefaultBotId],
  );
  const regularUnreadCount = notificationSummary.regularUnreadMessages;
  const botlessUnreadCount = notificationSummary.botlessUnreadMessages;
  const interventionCount = notificationSummary.humanInterventionThreads;

  const appointmentCount = notificationSummary.appointmentThreads;

  const handleLogout = async () => {
    await dispatch(logoutThunk()).unwrap();
    navigate('/login');
  };

  const [jwtSecondsLeft, setJwtSecondsLeft] = React.useState<number | null>(null);
  const [jwtExpired, setJwtExpired] = React.useState(false);
  const autoLogoutRef = React.useRef(false);

  React.useEffect(() => {
    const compute = () => {
      const tkn = localStorage.getItem('jwtToken');
      setJwtSecondsLeft(getSecondsLeft(tkn));
      setJwtExpired(isExpired(tkn, 5));
    };
    compute();
    const id = window.setInterval(compute, 15000);
    const onLogin = () => compute();
    const onLogout = () => compute();
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      clearInterval(id);
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, []);

  React.useEffect(() => {
    if (jwtExpired && !autoLogoutRef.current) {
      autoLogoutRef.current = true;
      handleLogout();
    }
  }, [jwtExpired]);

  const igUsername: string | null = connectionStatus?.ig_username || null;
  const igAvatar: string | null = connectionStatus?.profile_picture_url || null;
  const comercioLabel = user?.comercioKey || 'deviluppercase_tienda';

  const labelKey = (to: string) => {
    switch (to) {
      case '/products': return 'products';
      case '/orders': return 'orders';
      case '/bookings': return 'bookings';
      case '/bookings/schedules': return 'bookingsSchedules';
      case '/delivery-settings': return 'deliverySettings';
      case '/test-api': return 'testApi';
      case '/bots-config': return 'botsConfig';
      case '/workflows': return 'workflows';
      case '/post-bot': return 'postBot';
      case '/stats': return 'stats';
      case '/tags-config': return 'tags';
      case '/invoices': return 'invoices';
      case '/clinics': return 'clinics';
      case '/clinics/dashboard': return 'clinicsDashboard';
      case '/clinics/doctors': return 'clinicsDoctors';
      case '/clinics/patients': return 'clinicsPatients';
      case '/clinics/appointments': return 'clinicsAppointments';
      default: return 'products';
    }
  };

  return (
    <>
      {isCollapsed && openCollapsedMenu && (
        <div data-ui="main-sidebar-region-001" className="fixed inset-0 z-30" onClick={() => setOpenCollapsedMenu(null)} />
      )}
      <aside
        data-ui="main-sidebar-desktop"
        data-theme={theme}
        aria-label={t('sidebar.navigationLabel', 'Main navigation')}
        className={`app-sidebar-theme hidden lg:block fixed left-0 top-0 bg-black/30 backdrop-blur-md border-r border-white/10 shadow-sm transition-[width] duration-200 ease-in-out z-40 ${isCollapsed ? 'overflow-visible' : 'overflow-hidden'}`}
        style={{ width, height: '100vh' }}
      >
        <div data-ui="main-sidebar-layout" className="h-full flex flex-col">
          {/* Header Toggle */}
          <div data-ui="main-sidebar-brand-header" className={`h-16 flex items-center shrink-0 ${isCollapsed ? 'justify-center px-2' : 'justify-between px-5'} border-b border-white/10`}>
            {!isCollapsed && (
              <div data-ui="main-sidebar-region-002" className="flex flex-col items-start justify-center cursor-pointer select-none" onClick={() => navigate('/clinics/dashboard')}>
                <div data-ui="main-sidebar-region-003" className="flex items-center gap-1.5">
                  <img
                    src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_logo.svg"
                    alt="Taratic Logo"
                    className="h-6.5 w-auto object-contain"
                  />
                  <img
                    src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_letras.svg"
                    alt="TARATIC"
                    className="h-4.5 w-auto object-contain"
                  />
                </div>
                <span className="text-[10px] font-bold text-blue-400 tracking-[0.2em] uppercase pl-0.5 -mt-0.5">
                  Clinicas
                </span>
              </div>
            )}

            <button
              className={`flex items-center justify-center h-8 w-8 rounded-full transition-all duration-300 border border-white/10 cursor-pointer ${hoveredItemId === 'toggle'
                ? 'text-white bg-blue-600/80 shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                : 'text-white bg-blue-600/80 backdrop-blur-sm shadow-inner-glow shadow-inner-glow-white-sm text-shadow-glow-white'
                }`}
              onClick={() => dispatch(uiActions.toggleCollapse())}
              onMouseEnter={() => setHoveredItemId('toggle')}
              onMouseLeave={() => setHoveredItemId(null)}
              title={isCollapsed ? t('nav.expand') : t('nav.collapse')}
            >
              <FontAwesomeIcon icon={isCollapsed ? faIndent : faAnglesLeft} className="text-sm" />
            </button>
          </div>

          <nav data-ui="main-sidebar-navigation" className={`flex-1 py-1 mt-2 ${isCollapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
            {/* Notifications */}
            <div data-ui="main-sidebar-region-004" className="relative flex flex-col">
              <button
                data-ui="main-sidebar-messages-trigger"
                data-tour="notifications"
                onClick={() => dispatch(openChatUI())}
                onMouseEnter={() => setHoveredItemId('notifications')}
                onMouseLeave={() => setHoveredItemId(null)}
                className={`group flex items-center gap-3 mx-2 my-1 rounded-[1.75rem] px-3 py-2.5 text-sm transition-all duration-300 font-semibold cursor-pointer ${hoveredItemId === 'notifications'
                  ? 'text-white bg-blue-600/80 shadow-inner-glow shadow-inner-glow-white-sm text-shadow-glow-white'
                  : 'text-white bg-transparent hover:bg-white/5'
                  }`}
                title={t('nav.notifications.title', 'Notificaciones')}
              >
                <div data-ui="main-sidebar-messages-icon" className="relative flex items-center justify-center shrink-0">
                  {isCollapsed && regularUnreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold z-10 shadow-sm border border-black/20">
                      {regularUnreadCount > 99 ? '99+' : regularUnreadCount}
                    </span>
                  )}
                  {isCollapsed && botlessUnreadCount > 0 && (
                    <span
                      className="absolute -bottom-1.5 -right-2 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold z-10 shadow-[0_0_8px_rgba(249,115,22,0.65)] border border-black/20"
                      title="Mensajes sin bot disponibles"
                    >
                      {botlessUnreadCount > 99 ? '99+' : botlessUnreadCount}
                    </span>
                  )}
                  {isCollapsed && appointmentCount > 0 && (
                    <span className={`absolute -top-1.5 ${interventionCount > 0 ? '-left-6' : '-left-2'} inline-flex items-center justify-center gap-0.5 min-w-[18px] h-4 px-1 rounded-full bg-emerald-500 text-black text-[9px] font-bold z-10 shadow-[0_0_8px_rgba(16,185,129,0.6)] border border-black/20 animate-pulse`}>
                      <FontAwesomeIcon icon={faCalendarCheck} className="w-2 h-2" />
                      {appointmentCount > 99 ? '99+' : appointmentCount}
                    </span>
                  )}
                  {isCollapsed && interventionCount > 0 && (
                    <span className="absolute -top-1.5 -left-2 inline-flex items-center justify-center gap-0.5 min-w-[18px] h-4 px-1 rounded-full bg-yellow-500 text-black text-[9px] font-bold z-10 shadow-[0_0_8px_rgba(234,179,8,0.6)] border border-black/20 animate-pulse">
                      <FontAwesomeIcon icon={faUser} className="w-2 h-2" />
                      {interventionCount > 99 ? '99+' : interventionCount}
                    </span>
                  )}
                  <FontAwesomeIcon icon={faBell} className={`${isCollapsed ? 'mx-auto' : ''} shrink-0 w-5 h-5 transition-all duration-300`}
                    style={hoveredItemId === 'notifications' ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                  />
                </div>
                {!isCollapsed && (
                  <span data-ui="main-sidebar-messages-label" className="truncate tracking-wide flex-1 min-w-0 text-left">
                    {t('nav.notifications.title', 'Notificaciones')}
                  </span>
                )}
                {!isCollapsed && (
                  <div data-ui="main-sidebar-messages-notification-badges" className="ml-auto flex items-center justify-end gap-1.5 shrink-0">
                    {regularUnreadCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold shadow-[0_2px_6px_rgba(239,68,68,0.35)] ring-1 ring-white/70" title="Mensajes pendientes">
                        {regularUnreadCount > 99 ? '99+' : regularUnreadCount}
                      </span>
                    )}
                    {botlessUnreadCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-orange-500 text-white text-[10px] font-extrabold shadow-[0_2px_6px_rgba(249,115,22,0.35)] ring-1 ring-white/70" title="Mensajes sin bot disponibles">
                        {botlessUnreadCount > 99 ? '99+' : botlessUnreadCount}
                      </span>
                    )}
                    {appointmentCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-emerald-500 text-black text-[10px] font-bold shadow-[0_2px_6px_rgba(16,185,129,0.3)] animate-pulse ring-1 ring-white/60" title="Citas pendientes">
                        <FontAwesomeIcon icon={faCalendarCheck} className="w-2.5 h-2.5" />
                        {appointmentCount > 99 ? '99+' : appointmentCount}
                      </span>
                    )}
                    {interventionCount > 0 && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-yellow-500 text-black text-[10px] font-bold shadow-[0_2px_6px_rgba(234,179,8,0.3)] animate-pulse ring-1 ring-white/60" title="Chats pausados">
                        <FontAwesomeIcon icon={faUser} className="w-2.5 h-2.5" />
                        {interventionCount > 99 ? '99+' : interventionCount}
                      </span>
                    )}
                    {threadsLoading && (
                      <span className="flex space-x-1 ml-1" title="Cargando mensajes...">
                        <span className="w-1.5 h-1.5 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s' }}></span>
                        <span className="w-1.5 h-1.5 bg-purple-300 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '1s' }}></span>
                        <span className="w-1.5 h-1.5 bg-pink-300 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '1s' }}></span>
                      </span>
                    )}
                  </div>
                )}
              </button>

              {isCollapsed && hoveredItemId === 'notifications' && !openCollapsedMenu && (
                <div data-ui="main-sidebar-region-006" className="absolute left-[76px] top-1/2 -translate-y-1/2 bg-[#1e1e24]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3.5 py-2 text-xs font-semibold text-white shadow-[0_0_20px_rgba(0,0,0,0.5)] z-50 whitespace-nowrap pointer-events-none flex items-center gap-2 animate-in fade-in zoom-in-95">
                  <span>{t('nav.notifications.title', 'Notificaciones')}</span>
                </div>
              )}
            </div>

            <div data-ui="main-sidebar-region-007" className="h-px bg-white/10 mx-4 my-2"></div>

            {MENU.filter(item => !item.featureKey || (enabledFeatures && enabledFeatures.includes(item.featureKey))).map((item) => {
              if (item.subItems) {
                const isExpanded = expandedMenus[item.to];

                return (
                  <div data-ui="main-sidebar-region-008" key={item.to} className="relative flex flex-col">
                    <button
                      onClick={() => {
                        if (isCollapsed) {
                          setOpenCollapsedMenu(prev => prev === item.to ? null : item.to);
                        } else {
                          setExpandedMenus(prev => ({ ...prev, [item.to]: !prev[item.to] }));
                        }
                      }}
                      onMouseEnter={(e) => handleMenuMouseEnter(item, e)}
                      onMouseLeave={handleMenuMouseLeave}
                      className={`group flex items-center justify-between gap-1.5 mx-2 my-1 rounded-[1.75rem] ${isCollapsed ? 'px-2.5 py-2.5' : 'px-3 py-2.5'} text-sm transition-all duration-300 font-semibold cursor-pointer ${hoveredItemId === item.to || (isCollapsed && (openCollapsedMenu === item.to || flyoutItem?.to === item.to))
                        ? 'text-white bg-black shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                        : 'text-white bg-transparent'
                        }`}
                    >
                      <div data-ui="main-sidebar-region-009" className={`flex items-center gap-3 overflow-hidden ${isCollapsed ? 'justify-center flex-1' : ''}`}>
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="shrink-0 w-5 h-5 transition-all duration-300"
                          style={hoveredItemId === item.to || (isCollapsed && (openCollapsedMenu === item.to || flyoutItem?.to === item.to)) ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                        />
                        {!isCollapsed && (
                          <span className="truncate tracking-wide">{t(`sidebar.menu.${labelKey(item.to)}`, item.label)}</span>
                        )}
                      </div>
                      {!isCollapsed ? (
                        <FontAwesomeIcon icon={faChevronDown} className={`shrink-0 transition-transform duration-300 text-xs ${isExpanded ? 'rotate-180' : ''}`} />
                      ) : (
                        <FontAwesomeIcon icon={faChevronRight} className={`shrink-0 text-[10px] transition-transform duration-300 ${(openCollapsedMenu === item.to || flyoutItem?.to === item.to) ? 'text-blue-400 rotate-90' : 'text-white/40 group-hover:text-white'}`} />
                      )}
                    </button>

                    {/* Expanded Submenu (when not collapsed) */}
                    {isExpanded && !isCollapsed && (
                      <div data-ui="main-sidebar-region-010" className="ml-8 mr-2 mt-1 flex flex-col gap-1 border-l border-white/10 pl-2">
                        {item.subItems.map((subItem) => (
                          <NavLink
                            key={subItem.to}
                            to={subItem.to}
                            end={subItem.to === '/bookings'}
                            onMouseEnter={() => setHoveredItemId(subItem.to)}
                            onMouseLeave={() => setHoveredItemId(null)}
                            className={({ isActive }) =>
                              [
                                `group flex items-center gap-2 rounded-xl px-2 py-2 text-xs transition-all duration-300 font-medium`,
                                isActive
                                  ? 'text-white bg-blue-600/50 shadow-inner-glow shadow-inner-glow-white-sm'
                                  : hoveredItemId === subItem.to
                                    ? 'text-white bg-black/60 shadow-inner-glow shadow-inner-glow-white-sm'
                                    : 'text-white bg-transparent hover:text-white'
                              ].join(' ')
                            }
                          >
                            <FontAwesomeIcon icon={subItem.icon} className="shrink-0 w-3.5 h-3.5" />
                            <span className="truncate">{t(`sidebar.menu.${labelKey(subItem.to)}`, subItem.label)}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div data-ui="main-sidebar-region-011" key={item.to} className="relative flex flex-col">
                  <NavLink
                    to={item.to}
                    data-tour={item.featureKey}
                    onMouseEnter={() => setHoveredItemId(item.to)}
                    onMouseLeave={() => setHoveredItemId(null)}
                    onClick={() => setOpenCollapsedMenu(null)}
                    className={({ isActive }) =>
                      [
                        `group flex items-center gap-3 mx-2 my-1 rounded-[1.75rem] px-3 py-2.5 text-sm transition-all duration-300 font-semibold`,
                        isActive
                          ? hoveredItemId === item.to
                            ? 'text-white bg-blue-700/80 shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                            : 'text-white bg-blue-600/80 shadow-inner-glow shadow-inner-glow-white-sm text-shadow-glow-white'
                          : hoveredItemId === item.to
                            ? 'text-white bg-black shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                            : 'text-white bg-transparent'
                      ].join(' ')
                    }
                    title={isCollapsed ? t(`sidebar.menu.${labelKey(item.to)}`, item.label) : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        <FontAwesomeIcon
                          icon={item.icon}
                          className={`${isCollapsed ? 'mx-auto' : ''} shrink-0 w-5 h-5 transition-all duration-300`}
                          style={isActive || hoveredItemId === item.to ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                        />
                        {!isCollapsed && (
                          <span className="truncate tracking-wide">{t(`sidebar.menu.${labelKey(item.to)}`, item.label)}</span>
                        )}
                      </>
                    )}
                  </NavLink>

                  {isCollapsed && hoveredItemId === item.to && !openCollapsedMenu && !flyoutItem && (
                    <div data-ui="main-sidebar-region-012" className="absolute left-[76px] top-1/2 -translate-y-1/2 bg-[#1e1e24]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3.5 py-2 text-xs font-semibold text-white shadow-[0_0_20px_rgba(0,0,0,0.5)] z-50 whitespace-nowrap pointer-events-none flex items-center gap-2 animate-in fade-in zoom-in-95">
                      <span>{t(`sidebar.menu.${labelKey(item.to)}`, item.label)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Bottom Section */}
          <div data-ui="main-sidebar-account-footer" className="border-t border-white/10 flex flex-col bg-black/30 backdrop-blur-md shrink-0">
            {jwtSecondsLeft !== null && jwtSecondsLeft <= 300 && jwtSecondsLeft > 0 && !isCollapsed && (
              <div data-ui="main-sidebar-region-013" className="bg-amber-500/20 text-amber-200 text-xs px-3 py-2 flex items-center justify-between border-b border-amber-500/30">
                <span>{formatPrettyLeft(jwtSecondsLeft)}</span>
                <button onClick={handleLogout} className="underline hover:text-white transition-colors">{t('nav.session.reauth', 'Re-auth')}</button>
              </div>
            )}

            {isCollapsed && (
              <div data-ui="main-sidebar-region-014"
                className="border-b border-white/5 cursor-pointer select-none group transition-all py-2 px-1 flex flex-col items-center justify-center"
                onClick={() => navigate('/clinics/dashboard')}
                title="Taratic Clínicas"
              >
                <div data-ui="main-sidebar-region-015" className="flex flex-col items-center justify-center w-full overflow-hidden">
                  <div data-ui="main-sidebar-region-016" className="flex items-center justify-center gap-1 w-full px-0.5">
                    <img
                      src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_logo.svg"
                      alt="Taratic Logo"
                      className="h-4 w-auto object-contain shrink-0 transition-transform group-hover:scale-105"
                    />
                    <img
                      src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_letras.svg"
                      alt="TARATIC"
                      className="h-2.5 max-w-[44px] object-contain shrink-0"
                    />
                  </div>
                  <span className="text-[7.5px] font-bold text-blue-400 tracking-[0.15em] uppercase mt-0.5 text-center whitespace-nowrap">
                    Clinicas
                  </span>
                </div>
              </div>
            )}

            <div data-ui="main-sidebar-region-017" className="p-3 relative flex items-center justify-between w-full">
              <div data-ui="main-sidebar-region-018" className={`flex items-center gap-3 overflow-hidden ${isCollapsed ? 'w-full justify-center' : ''}`}>
                <div data-ui="main-sidebar-region-019"
                  className={`flex-shrink-0 cursor-pointer transition-all duration-300 rounded-full ${isCollapsed ? 'hover:scale-105 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm' : ''}`}
                  onClick={() => setShowSettings(!showSettings)}
                  title={isCollapsed ? (igUsername ? `@${igUsername}` : (user?.email || t('settings.title', 'Configuración'))) : undefined}
                >
                  {igAvatar ? (
                    <>
                      <img
                        src={igAvatar}
                        alt={igUsername || ''}
                        className="w-9 h-9 rounded-full object-cover shadow-md border border-white/10"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.nextElementSibling) {
                            e.currentTarget.nextElementSibling.classList.remove('hidden');
                            e.currentTarget.nextElementSibling.classList.add('inline-flex');
                          }
                        }}
                      />
                      <span className="hidden items-center justify-center h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold shadow-md border border-white/10">
                        {(igUsername || user?.email || 'U')[0]?.toUpperCase()}
                      </span>
                    </>
                  ) : (
                    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold shadow-md border border-white/10">
                      {(igUsername || user?.email || 'U')[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
                {!isCollapsed && (
                  <div data-ui="main-sidebar-region-020" className="overflow-hidden flex-1 cursor-pointer" onClick={() => setShowSettings(!showSettings)}>
                    <p className="text-sm font-semibold text-white truncate">{igUsername ? `@${igUsername}` : user?.email}</p>
                    <p className="text-xs text-white/80 truncate">{comercioLabel}</p>
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <button
                  data-ui="main-sidebar-settings-trigger"
                  data-selected={showSettings}
                  aria-expanded={showSettings}
                  onClick={() => setShowSettings(!showSettings)}
                  className={`shrink-0 p-2 rounded-full cursor-pointer transition-all duration-300 hover:bg-white/10 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm ${showSettings ? 'bg-white/20 shadow-inner-glow shadow-inner-glow-white-sm' : ''}`}
                  title={t('settings.title', 'Configuración')}
                >
                  <FontAwesomeIcon icon={faCog} className="text-white w-4 h-4 transition-transform duration-300 hover:rotate-90" />
                </button>
              )}

              {showSettings && (
                <>
                  <div data-ui="main-sidebar-region-021" className="fixed inset-0 z-40" onClick={() => setShowSettings(false)}></div>
                  <div data-ui="main-sidebar-settings-menu" className={`absolute bottom-full mb-3 ${isCollapsed ? 'left-2 w-52' : 'left-3 right-3'} bg-black/90 backdrop-filter backdrop-blur-xl rounded-2xl shadow-[inset_0_0_15px_rgba(156,163,175,0.45)] p-2 z-50 overflow-hidden transform transition-all origin-bottom border-none space-y-1`}>
                    <div data-ui="main-sidebar-region-022" className="px-3.5 py-2 border-b border-white/10 mb-1">
                      <p className="text-xs text-white/90 font-bold uppercase tracking-wider">{t('settings.title', 'Ajustes')}</p>
                    </div>

                    <div data-ui="main-sidebar-region-023" className="px-3.5 py-2.5 rounded-xl hover:bg-white/10 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm hover:text-shadow-glow-white transition-all duration-300 flex items-center justify-between group cursor-pointer">
                      <span className="text-sm text-white font-medium">{t('nav.changeLanguage', 'Cambiar idioma')}</span>
                      <div data-ui="main-sidebar-region-024" className="flex items-center gap-1 shrink-0">
                        <SlidingPills
                          options={[
                            {
                              id: 'es',
                              label: 'ES',
                              customIcon: <FlagES className="w-4 h-3 rounded-sm shadow-sm shrink-0" />
                            },
                            {
                              id: 'en',
                              label: 'EN',
                              customIcon: <FlagGB className="w-4 h-3 rounded-sm shadow-sm shrink-0" />
                            }
                          ]}
                          activeId={(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'}
                          onChange={(id) => i18n.changeLanguage(id)}
                          variant={theme}
                          size="xs"
                        />
                      </div>
                    </div>

                    <div data-ui="main-sidebar-theme-setting" className="px-3.5 py-2.5 rounded-xl hover:bg-white/10 transition-all duration-300 flex items-center justify-between gap-3">
                      <span className="text-sm text-white font-medium inline-flex items-center gap-2">
                        <FontAwesomeIcon icon={theme === 'dark' ? faMoon : faSun} className="w-4 h-4 text-blue-400" />
                        {theme === 'dark'
                          ? t('settings.theme.dark', 'Dark mode')
                          : t('settings.theme.light', 'Light mode')}
                      </span>
                      <GhostSwitch
                        checked={theme === 'dark'}
                        onChange={(darkEnabled) => dispatch(uiActions.setTheme(darkEnabled ? 'dark' : 'light'))}
                        ariaLabel={t('settings.theme.toggle', 'Switch between light and dark mode')}
                        variant={theme}
                        size="sm"
                      />
                    </div>

                    <button
                      data-tour="settings"
                      onClick={() => {
                        setShowSettings(false);
                        navigate('/settings');
                      }}
                      className={`group w-full cursor-pointer flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all duration-300 text-sm font-semibold border-none ${
                        location.pathname.startsWith('/settings')
                          ? 'text-white bg-blue-600/50 shadow-inner-glow shadow-inner-glow-white-sm text-shadow-glow-white'
                          : 'text-white bg-transparent hover:bg-white/10 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm hover:text-shadow-glow-white'
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faCog}
                        className="w-4 h-4 shrink-0 transition-all duration-300 group-hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]"
                        style={location.pathname.startsWith('/settings') ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8))' } : {}}
                      />
                      <span className="truncate">{t('nav.settings', 'Ajustes')}</span>
                    </button>

                    <button
                      onClick={() => {
                        setShowSettings(false);
                        handleLogout();
                      }}
                      className="group w-full cursor-pointer flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-red-400 hover:text-white hover:bg-red-600/40 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm hover:text-shadow-glow-white transition-all duration-300 text-sm font-semibold border-none"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4 shrink-0 transition-all duration-300 group-hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.8)]" />
                      <span className="truncate">{t('nav.logout', 'Cerrar sesión')}</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Portaled Collapsed Flyout Popover for Submenu */}
      {isCollapsed && flyoutItem && flyoutPos && createPortal(
        <div
          data-ui="main-sidebar-collapsed-flyout"
          data-theme={theme}
          className="app-sidebar-theme fixed z-50 animate-in fade-in zoom-in-95 pointer-events-auto"
          style={{ top: flyoutPos.top, left: flyoutPos.left }}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <div data-ui="main-sidebar-region-025" className="bg-black/40 backdrop-filter backdrop-blur-xl border border-white/10 rounded-2xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md p-2 min-w-[200px] flex flex-col gap-1">
            <div data-ui="main-sidebar-region-026" className="px-2 py-1.5 border-b border-white/10 flex items-center justify-between gap-2 mb-1">
              <div data-ui="main-sidebar-region-027" className="flex items-center gap-2 font-bold text-xs text-white">
                <FontAwesomeIcon icon={flyoutItem.icon} className="text-blue-400 w-3.5 h-3.5" />
                <span>{t(`sidebar.menu.${labelKey(flyoutItem.to)}`, flyoutItem.label)}</span>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="text-[10px] text-white/40" />
            </div>

            <div data-ui="main-sidebar-region-028" className="flex flex-col gap-1">
              {flyoutItem.subItems?.map((subItem) => (
                <NavLink
                  key={subItem.to}
                  to={subItem.to}
                  end={subItem.to === '/bookings'}
                  onClick={() => {
                    clearHoverTimeout();
                    setFlyoutItem(null);
                    setFlyoutPos(null);
                    setHoveredItemId(null);
                    setOpenCollapsedMenu(null);
                  }}
                  onMouseEnter={() => {
                    clearHoverTimeout();
                    setHoveredItemId(subItem.to);
                  }}
                  onMouseLeave={() => {
                    setHoveredItemId(flyoutItem.to);
                  }}
                  className={({ isActive }) =>
                    [
                      `group flex items-center gap-2 rounded-xl px-2 py-2 text-xs transition-all duration-300 font-medium cursor-pointer`,
                      isActive
                        ? 'text-white bg-blue-600/50 shadow-inner-glow shadow-inner-glow-white-sm'
                        : hoveredItemId === subItem.to
                          ? 'text-white bg-black/60 shadow-inner-glow shadow-inner-glow-white-sm'
                          : 'text-white bg-transparent hover:bg-black/60 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm'
                    ].join(' ')
                  }
                >
                  <FontAwesomeIcon icon={subItem.icon} className="shrink-0 w-3.5 h-3.5" />
                  <span className="truncate">{t(`sidebar.menu.${labelKey(subItem.to)}`, subItem.label)}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}    </>
  );
};

const MobileSidebar: React.FC<{
  enabledFeatures: string[] | null;
  channelDefaultBotId: number | null | undefined;
}> = ({ enabledFeatures, channelDefaultBotId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileOpen = useAppSelector((s) => s.ui.isMobileOpen);
  const mobileSidebarView = useAppSelector((s) => s.ui.mobileSidebarView);
  const hasUnsavedSettingsChanges = useAppSelector((s) => s.ui.hasUnsavedSettingsChanges);
  const theme = useAppSelector((s) => s.ui.theme);
  const connectionStatus = useAppSelector((s) => s.chat.connectionStatus);
  const user = useAppSelector((s) => s.auth.user);
  const { t, i18n } = useTranslation();
  const unreadCount = useAppSelector((s) => s.chat.unreadCount);
  const threads = useAppSelector((s) => s.chat.threads);
  const [hoveredItemId, setHoveredItemId] = React.useState<string | null>(null);
  const [showSettings, setShowSettings] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState<Record<string, boolean>>({ '/clinics': false, '/bookings': false });
  const launcherRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const wasMobileOpenRef = React.useRef(false);
  const drawerHistoryEntryRef = React.useRef(false);

  React.useEffect(() => {
    const onPopState = () => {
      if (!drawerHistoryEntryRef.current) return;
      drawerHistoryEntryRef.current = false;
      dispatch(uiActions.closeMobile());
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [dispatch]);

  React.useEffect(() => {
    const wasOpen = wasMobileOpenRef.current;
    if (isMobileOpen && !wasOpen) {
      window.history.pushState(
        { ...window.history.state, taraticMobileDrawer: true },
        '',
        window.location.href,
      );
      drawerHistoryEntryRef.current = true;
    } else if (!isMobileOpen && wasOpen && drawerHistoryEntryRef.current) {
      drawerHistoryEntryRef.current = false;
      window.history.back();
    }

    if (isMobileOpen) {
      window.requestAnimationFrame(() => {
        panelRef.current
          ?.querySelector<HTMLElement>(`[data-drawer-view="${mobileSidebarView}"] [data-drawer-autofocus]`)
          ?.focus();
      });
    } else if (wasOpen) {
      launcherRef.current?.focus();
    }
    wasMobileOpenRef.current = isMobileOpen;
  }, [isMobileOpen, mobileSidebarView]);

  React.useEffect(() => {
    if (!isMobileOpen) return;
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const activeView = panelRef.current?.querySelector<HTMLElement>(`[data-drawer-view="${mobileSidebarView}"]`);
      const focusable = Array.from(activeView?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? []).filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => document.removeEventListener('keydown', trapFocus);
  }, [isMobileOpen, mobileSidebarView]);

  const closeMobileDrawer = React.useCallback(() => {
    dispatch(uiActions.closeMobile());
  }, [dispatch]);

  const navigateFromDrawer = React.useCallback((to: string, preserveScroll = false) => {
    const replacesDrawerEntry = drawerHistoryEntryRef.current;
    drawerHistoryEntryRef.current = false;
    dispatch(uiActions.closeMobile());
    navigate(to, { replace: replacesDrawerEntry });

    if (!preserveScroll) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const heading = document.querySelector<HTMLElement>('[data-ui="settings-content"] h1, [data-ui="settings-content"] h2');
          if (heading) {
            heading.tabIndex = -1;
            heading.focus();
          }
        });
      });
    }
  }, [dispatch, navigate]);

  const notificationSummary = React.useMemo(
    () => summarizeChatNotifications(threads, unreadCount, channelDefaultBotId),
    [threads, unreadCount, channelDefaultBotId],
  );
  const regularUnreadCount = notificationSummary.regularUnreadMessages;
  const botlessUnreadCount = notificationSummary.botlessUnreadMessages;
  const interventionCount = notificationSummary.humanInterventionThreads;
  const appointmentCount = notificationSummary.appointmentThreads;

  const handleLogout = async () => {
    await dispatch(logoutThunk()).unwrap();
    navigateFromDrawer('/login');
  };

  const labelKey = (to: string) => {
    switch (to) {
      case '/products': return 'products';
      case '/orders': return 'orders';
      case '/bookings': return 'bookings';
      case '/bookings/schedules': return 'bookingsSchedules';
      case '/reservations': return 'reservations';
      case '/tables': return 'tables';
      case '/schedules': return 'schedules';
      case '/delivery-settings': return 'deliverySettings';
      case '/test-api': return 'testApi';
      case '/bots-config': return 'botsConfig';
      case '/workflows': return 'workflows';
      case '/post-bot': return 'postBot';
      case '/stats': return 'stats';
      case '/tags-config': return 'tags';
      case '/invoices': return 'invoices';
      case '/clinics': return 'clinics';
      case '/clinics/dashboard': return 'clinicsDashboard';
      case '/clinics/patients': return 'clinicsPatients';
      case '/clinics/appointments': return 'clinicsAppointments';
      default: return 'products';
    }
  };

  const panelWidth = 280;

  const igUsername: string | null = connectionStatus?.ig_username || null;
  const igAvatar: string | null = connectionStatus?.profile_picture_url || null;
  const comercioLabel: string | null = user?.comercioKey || 'deviluppercase_tienda';

  const [jwtSecondsLeft, setJwtSecondsLeft] = React.useState<number | null>(null);
  React.useEffect(() => {
    const compute = () => {
      const t = typeof window !== 'undefined' ? localStorage.getItem('jwtToken') : null;
      setJwtSecondsLeft(getSecondsLeft(t));
    };
    compute();
    const id = window.setInterval(compute, 15000);
    const onLogin = () => compute();
    const onLogout = () => compute();
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      clearInterval(id);
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, []);

  return (
    <>
      {/* Floating Mobile Toggle Button */}
      {!isMobileOpen && (
        <button
          ref={launcherRef}
          data-ui="main-sidebar-mobile-launcher"
          data-theme={theme}
          onClick={() => dispatch(
            location.pathname.startsWith('/settings')
              ? uiActions.openMobileSettings()
              : uiActions.openMobile(),
          )}
          className="app-sidebar-theme lg:hidden fixed top-4 left-4 z-40 p-2.5 rounded-full bg-black/50 backdrop-blur-md text-blue-400 border border-white/10 shadow-lg hover:bg-black/70 transition-all"
          aria-label={t('nav.openMenu')}
        >
          <FontAwesomeIcon icon={faIndent} className="h-5 w-5" />
        </button>
      )}

      <div data-ui="main-sidebar-mobile-layer" className={`lg:hidden fixed inset-0 z-50 ${isMobileOpen ? '' : 'pointer-events-none'}`}>
        {/* Backdrop */}
        <div data-ui="main-sidebar-region-029"
          className={`absolute inset-0 bg-black/30 transition-opacity ${isMobileOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={closeMobileDrawer}
        />

        {/* Panel */}
        <div
          ref={panelRef}
          data-ui="main-sidebar-mobile"
          data-theme={theme}
          className={`app-sidebar-theme absolute top-0 left-0 overflow-hidden bg-black/30 backdrop-blur-md border-r border-white/10 shadow-xl h-screen transition-transform duration-200 motion-reduce:transition-none ease-in-out ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} z-50`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!isMobileOpen}
          style={{ width: panelWidth }}
        >
          <div
            data-ui="main-sidebar-mobile-layout"
            data-drawer-view="main"
            aria-hidden={mobileSidebarView !== 'main'}
            inert={mobileSidebarView !== 'main'}
            className={`absolute inset-0 h-full flex flex-col transition-transform duration-200 motion-reduce:transition-none ease-in-out ${mobileSidebarView === 'settings' ? '-translate-x-full pointer-events-none' : 'translate-x-0'}`}
          >
            <div data-ui="main-sidebar-mobile-brand-header" className="h-16 px-4 border-b border-white/10 flex items-center justify-between shrink-0">
              <div data-ui="main-sidebar-region-030" className="flex flex-col items-start justify-center cursor-pointer select-none" onClick={() => navigateFromDrawer('/clinics/dashboard')}>
                <div data-ui="main-sidebar-region-031" className="flex items-center gap-1.5">
                  <img
                    src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_logo.svg"
                    alt="Taratic Logo"
                    className="h-6.5 w-auto object-contain"
                  />
                  <img
                    src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_letras.svg"
                    alt="TARATIC"
                    className="h-4.5 w-auto object-contain"
                  />
                </div>
                <span className="text-[10px] font-bold text-blue-400 tracking-[0.2em] uppercase pl-0.5 -mt-0.5">
                  Clinicas
                </span>
              </div>
              <button
                data-drawer-autofocus
                onClick={closeMobileDrawer}
                className="p-2 rounded hover:bg-white/10 cursor-pointer"
                aria-label={t('sidebar.actions.close')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {/* IG + Comercio (only non-desktop) */}
            <div data-ui="main-sidebar-region-032" className="px-3 py-2 border-b border-white/10">
              {igUsername && (
                <div data-ui="main-sidebar-region-033" className="flex items-center gap-2 text-sm text-white/80">
                  <div data-ui="main-sidebar-region-034" className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 text-white flex items-center justify-center text-xs">
                    {igUsername.charAt(0).toUpperCase()}
                  </div>
                  <span>@{igUsername}</span>
                </div>
              )}
              {comercioLabel && (
                <div data-ui="main-sidebar-region-035" className="mt-1 text-xs text-white/60">
                  {t('sidebar.mobile.commerce')} {comercioLabel}
                </div>
              )}
            </div>

            <nav data-ui="main-sidebar-mobile-navigation" className="flex-1 py-2 overflow-y-auto mt-1">
              {/* Notifications */}
              <button
                data-ui="main-sidebar-mobile-messages-trigger"
                onClick={() => { dispatch(openChatUI()); closeMobileDrawer(); }}
                onMouseEnter={() => setHoveredItemId('notifications')}
                onMouseLeave={() => setHoveredItemId(null)}
                className={`w-[calc(100%-1rem)] group flex items-center gap-3 mx-2 my-1 rounded-[1.75rem] px-3 py-2.5 text-sm transition-all duration-300 font-semibold border cursor-pointer ${hoveredItemId === 'notifications'
                  ? 'text-white bg-blue-700/80 shadow-inner-glow shadow-inner-glow-white-sm border-white/40 text-shadow-glow-white'
                  : 'text-white bg-transparent border-transparent hover:bg-white/5'
                  }`}
                title={t('nav.notifications.title', 'Notificaciones')}
              >
                <div data-ui="main-sidebar-mobile-messages-icon" className="relative flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faBell} className="shrink-0 w-5 h-5 transition-all duration-300"
                    style={hoveredItemId === 'notifications' ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                  />
                </div>
                <span data-ui="main-sidebar-mobile-messages-label" className="truncate tracking-wide flex-1 min-w-0 text-left">{t('nav.notifications.title', 'Notificaciones')}</span>
                <div data-ui="main-sidebar-mobile-messages-notification-badges" className="ml-auto flex items-center gap-1.5 shrink-0">
                  {regularUnreadCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold shadow-[0_2px_6px_rgba(239,68,68,0.35)] ring-1 ring-white/70" title="Mensajes pendientes">
                      {regularUnreadCount > 99 ? '99+' : regularUnreadCount}
                    </span>
                  )}
                  {botlessUnreadCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-orange-500 text-white text-[10px] font-extrabold shadow-[0_2px_6px_rgba(249,115,22,0.35)] ring-1 ring-white/70" title="Mensajes sin bot disponibles">
                      {botlessUnreadCount > 99 ? '99+' : botlessUnreadCount}
                    </span>
                  )}
                </div>
              </button>

              <div data-ui="main-sidebar-region-037" className="h-px bg-white/10 mx-4 my-2"></div>

              {MENU.filter(item => !item.featureKey || (enabledFeatures && enabledFeatures.includes(item.featureKey))).map((item) => {
                if (item.subItems) {
                  const isExpanded = expandedMenus[item.to];
                  return (
                    <div data-ui="main-sidebar-region-038" key={item.to} className="flex flex-col">
                      <button
                        onClick={() => setExpandedMenus(prev => ({ ...prev, [item.to]: !prev[item.to] }))}
                        onMouseEnter={() => setHoveredItemId(item.to)}
                        onMouseLeave={() => setHoveredItemId(null)}
                        className={`group flex items-center justify-between gap-3 mx-2 my-1 rounded-[1.75rem] px-3 py-2.5 text-sm transition-all duration-300 font-semibold cursor-pointer ${hoveredItemId === item.to
                          ? 'text-white bg-black shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                          : 'text-white bg-transparent'
                          }`}
                      >
                        <div data-ui="main-sidebar-region-039" className="flex items-center gap-3 overflow-hidden">
                          <FontAwesomeIcon
                            icon={item.icon}
                            className="shrink-0 w-5 h-5 transition-all duration-300"
                            style={hoveredItemId === item.to ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                          />
                          <span className="truncate tracking-wide">{t(`sidebar.menu.${labelKey(item.to)}`, item.label)}</span>
                        </div>
                        <FontAwesomeIcon icon={faChevronDown} className={`shrink-0 transition-transform duration-300 text-xs ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {isExpanded && (
                        <div data-ui="main-sidebar-region-040" className="ml-8 mr-2 mt-1 flex flex-col gap-1 border-l border-white/10 pl-2">
                          {item.subItems.map((subItem) => (
                            <NavLink
                              key={subItem.to}
                              to={subItem.to}
                              end={subItem.to === '/bookings'}
                              onClick={(event) => {
                                event.preventDefault();
                                navigateFromDrawer(subItem.to);
                              }}
                              onMouseEnter={() => setHoveredItemId(subItem.to)}
                              onMouseLeave={() => setHoveredItemId(null)}
                              className={({ isActive }) =>
                                [
                                  `group flex items-center gap-2 rounded-xl px-2 py-2 text-xs transition-all duration-300 font-medium`,
                                  isActive
                                    ? 'text-white bg-blue-600/50 shadow-inner-glow shadow-inner-glow-white-sm'
                                    : hoveredItemId === subItem.to
                                      ? 'text-white bg-black/60 shadow-inner-glow shadow-inner-glow-white-sm'
                                      : 'text-white bg-transparent'
                                ].join(' ')
                              }
                            >
                              <FontAwesomeIcon icon={subItem.icon} className="shrink-0 w-3.5 h-3.5" />
                              <span className="truncate">{t(`sidebar.menu.${labelKey(subItem.to)}`, subItem.label)}</span>
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={(event) => {
                      event.preventDefault();
                      navigateFromDrawer(item.to);
                    }}
                    onMouseEnter={() => setHoveredItemId(item.to)}
                    onMouseLeave={() => setHoveredItemId(null)}
                    className={({ isActive }) =>
                      [
                        'group flex items-center gap-3 mx-2 my-1 rounded-[1.75rem] px-3 py-2.5 text-sm transition-all duration-300 font-semibold',
                        isActive
                          ? hoveredItemId === item.to
                            ? 'text-white bg-blue-700/80 shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                            : 'text-white bg-blue-700/80 shadow-inner-glow shadow-inner-glow-white-md text-shadow-glow-white'
                          : hoveredItemId === item.to
                            ? 'text-white bg-black shadow-inner-glow shadow-inner-glow-white-2xl text-shadow-glow-white'
                            : 'text-white bg-transparent'
                      ].join(' ')
                    }
                    title={t(`sidebar.menu.${labelKey(item.to)}`, item.label)}
                  >
                    {({ isActive }) => (
                      <>
                        <FontAwesomeIcon
                          icon={item.icon}
                          className="shrink-0 w-5 h-5 transition-all duration-300"
                          style={isActive || hoveredItemId === item.to ? { filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4))' } : {}}
                        />
                        <span className="truncate tracking-wide">{t(`sidebar.menu.${labelKey(item.to)}`, item.label)}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>

            {/* Bottom Section Mobile */}
            <div data-ui="main-sidebar-mobile-account-footer" className="border-t border-white/10 flex flex-col bg-black/30 backdrop-blur-md shrink-0">
              {jwtSecondsLeft !== null && jwtSecondsLeft <= 300 && jwtSecondsLeft > 0 && (
                <div data-ui="main-sidebar-region-041" className="bg-amber-500/20 text-amber-200 text-xs px-3 py-2 flex items-center justify-between border-b border-amber-500/30">
                  <span>{formatPrettyLeft(jwtSecondsLeft)}</span>
                  <button onClick={handleLogout} className="underline hover:text-white transition-colors">{t('nav.session.reauth', 'Re-auth')}</button>
                </div>
              )}

              <div data-ui="main-sidebar-region-042" className="p-3 relative flex items-center justify-between w-full">
                <div data-ui="main-sidebar-region-043" className="flex items-center gap-3 overflow-hidden cursor-pointer" onClick={() => setShowSettings(!showSettings)}>
                  <div data-ui="main-sidebar-region-044" className="flex-shrink-0 cursor-pointer" onClick={() => setShowSettings(!showSettings)}>
                    {igAvatar ? (
                      <>
                        <img
                          src={igAvatar}
                          alt={igUsername || ''}
                          className="w-9 h-9 rounded-full object-cover shadow-md"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            if (e.currentTarget.nextElementSibling) {
                              e.currentTarget.nextElementSibling.classList.remove('hidden');
                              e.currentTarget.nextElementSibling.classList.add('inline-flex');
                            }
                          }}
                        />
                        <span className="hidden items-center justify-center h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold shadow-md">
                          {(igUsername || user?.email || 'U')[0]?.toUpperCase()}
                        </span>
                      </>
                    ) : (
                      <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold shadow-md">
                        {(igUsername || user?.email || 'U')[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div data-ui="main-sidebar-region-045" className="overflow-hidden flex-1">
                    <p className="text-sm font-semibold text-white truncate">{igUsername ? `@${igUsername}` : user?.email}</p>
                    <p className="text-xs text-white/80 truncate">{comercioLabel}</p>
                  </div>
                </div>

                <button
                  data-ui="main-sidebar-mobile-settings-trigger"
                  data-selected={showSettings}
                  aria-expanded={showSettings}
                  onClick={() => setShowSettings(!showSettings)}
                  className={`shrink-0 p-2 rounded-full cursor-pointer transition-all duration-300 hover:bg-white/10 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm ${showSettings ? 'bg-white/20 shadow-inner-glow shadow-inner-glow-white-sm' : ''}`}
                  title={t('settings.title', 'Configuración')}
                >
                  <FontAwesomeIcon icon={faCog} className="text-white w-4 h-4 transition-transform duration-300 hover:rotate-90" />
                </button>

                {/* Settings Popover Mobile */}
                {showSettings && (
                  <>
                    <div data-ui="main-sidebar-region-046" className="fixed inset-0 z-40" onClick={() => setShowSettings(false)}></div>
                    <div data-ui="main-sidebar-mobile-settings-menu" className="absolute bottom-full mb-3 left-3 right-3 bg-[#1e1e24]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_0_30px_rgba(0,0,0,0.5)] py-2 z-50 overflow-hidden transform transition-all origin-bottom">
                      <div data-ui="main-sidebar-region-047" className="px-4 py-2 border-b border-white/5 mb-1 flex items-center justify-between">
                        <p className="text-xs text-white/80 font-semibold uppercase tracking-wider">{t('settings.title', 'Configuración')}</p>
                      </div>

                      <div data-ui="main-sidebar-region-048" className="px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors group">
                        <span className="text-sm text-white">{t('nav.changeLanguage', 'Idioma')}</span>
                        <div data-ui="main-sidebar-region-049" className="flex items-center space-x-1.5 ml-auto">
                          {botlessUnreadCount > 0 && (
                            <div data-ui="main-sidebar-region-050"
                              className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-orange-500/20 border border-orange-500/50 shadow-[0_0_8px_rgba(249,115,22,0.45)] transition-all"
                              title="Mensajes sin bot disponibles"
                            >
                              <span className="px-1.5 text-[11px] font-bold text-orange-400 drop-shadow-md">
                                {botlessUnreadCount > 99 ? '99+' : botlessUnreadCount}
                              </span>
                            </div>
                          )}
                          {appointmentCount > 0 && (
                            <div data-ui="main-sidebar-region-051"
                              className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-emerald-500/20 border border-emerald-500/50 shadow-[0_0_8px_rgba(16,185,129,0.3)] transition-all animate-pulse"
                              title="Citas Agendadas por IA pendientes de revisión"
                            >
                              <span className="px-1.5 text-[11px] font-bold text-emerald-400 drop-shadow-md">
                                {appointmentCount > 99 ? '99+' : appointmentCount}
                              </span>
                            </div>
                          )}
                          {interventionCount > 0 && (
                            <div data-ui="main-sidebar-region-052" className="flex items-center justify-center min-w-[20px] h-5 rounded-full bg-amber-500/20 border border-amber-500/50 shadow-[0_0_8px_rgba(245,158,11,0.3)] transition-all animate-pulse">
                              <span className="px-1.5 text-[11px] font-bold text-amber-400 drop-shadow-md">
                                {interventionCount > 99 ? '99+' : interventionCount}
                              </span>
                            </div>
                          )}
                          <SlidingPills
                            options={[
                              {
                                id: 'es',
                                label: 'ES',
                                customIcon: <FlagES className="w-4 h-3 rounded-sm shadow-sm shrink-0" />
                              },
                              {
                                id: 'en',
                                label: 'EN',
                                customIcon: <FlagGB className="w-4 h-3 rounded-sm shadow-sm shrink-0" />
                              }
                            ]}
                            activeId={(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'}
                            onChange={(id) => i18n.changeLanguage(id)}
                            variant={theme}
                            size="xs"
                          />
                        </div>
                      </div>

                      <div data-ui="main-sidebar-mobile-theme-setting" className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/5 transition-colors">
                        <span className="text-sm text-white inline-flex items-center gap-2">
                          <FontAwesomeIcon icon={theme === 'dark' ? faMoon : faSun} className="w-4 h-4 text-blue-400" />
                          {theme === 'dark'
                            ? t('settings.theme.dark', 'Dark mode')
                            : t('settings.theme.light', 'Light mode')}
                        </span>
                        <GhostSwitch
                          checked={theme === 'dark'}
                          onChange={(darkEnabled) => dispatch(uiActions.setTheme(darkEnabled ? 'dark' : 'light'))}
                          ariaLabel={t('settings.theme.toggle', 'Switch between light and dark mode')}
                          variant={theme}
                          size="sm"
                        />
                      </div>

                      <button
                        onClick={() => {
                          setShowSettings(false);
                          dispatch(uiActions.openMobileSettings());
                        }}
                        aria-label={t('nav.settings', 'Ajustes Generales')}
                        className="w-full cursor-pointer flex items-center gap-3 px-4 py-3 mt-1 hover:bg-white/5 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm text-white transition-all duration-300 text-sm border-t border-white/5"
                      >
                        <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                        <span className="font-medium">{t('nav.settings', 'Ajustes Generales')}</span>
                        <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5 ml-auto text-white/70" aria-hidden="true" />
                      </button>

                      <button
                        onClick={() => {
                          setShowSettings(false);
                          void handleLogout();
                        }}
                        className="w-full cursor-pointer flex items-center gap-3 px-4 py-3 mt-1 hover:bg-red-500/20 hover:shadow-inner-glow hover:shadow-inner-glow-white-sm text-red-400 transition-all duration-300 text-sm border-t border-white/5"
                      >
                        <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4" />
                        <span className="font-medium">{t('nav.logout', 'Cerrar sesión')}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            data-ui="settings-mobile-sidebar"
            data-drawer-view="settings"
            aria-hidden={mobileSidebarView !== 'settings'}
            inert={mobileSidebarView !== 'settings'}
            className={`absolute inset-0 h-full flex flex-col bg-black/30 transition-transform duration-200 motion-reduce:transition-none ease-in-out ${mobileSidebarView === 'settings' ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
          >
            <div className="min-h-16 px-3 py-2 border-b border-white/10 shrink-0">
              <div className="flex items-center justify-between gap-2">
                <button
                  data-drawer-autofocus
                  type="button"
                  onClick={() => dispatch(uiActions.showMobileMain())}
                  className="inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-semibold text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  aria-label={t('settings.sidebar.mainMenu', 'Menú principal')}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
                  <span>{t('settings.sidebar.mainMenu', 'Menú principal')}</span>
                </button>
                <button
                  type="button"
                  onClick={closeMobileDrawer}
                  className="p-2 rounded hover:bg-white/10 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                  aria-label={t('sidebar.actions.close')}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
              <div className="flex items-center gap-3 px-2 pt-2 pb-1">
                <div className="w-9 h-9 rounded-xl bg-blue-600/30 text-blue-300 flex items-center justify-center shrink-0 shadow-inner-glow shadow-inner-glow-white-sm">
                  <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                </div>
                <h2 className="text-base font-bold text-white tracking-tight">
                  {t('settings.mobileTitle', 'Ajustes')}
                </h2>
              </div>
            </div>
            <div className="flex-1 min-h-0 p-3 flex flex-col">
              <SettingsNavigation
                onNavigate={(to, isActive) => {
                  if (!isActive && hasUnsavedSettingsChanges && !window.confirm(
                    t(
                      'settings.unsavedChanges.confirm',
                      'Tienes cambios sin guardar. ¿Quieres descartarlos y continuar?',
                    ),
                  )) {
                    return false;
                  }
                  if (!isActive) dispatch(uiActions.setSettingsUnsavedChanges(false));
                  navigateFromDrawer(to, isActive);
                  return false;
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Sidebar: React.FC = () => {
  const [enabledFeatures, setEnabledFeatures] = React.useState<string[] | null>(null);
  const { data: activeByChannel, isSuccess: activeByChannelLoaded } = useGetActiveByChannelQuery();
  const channelDefaultBotId = activeByChannelLoaded
    ? activeByChannel?.instagram?.id ?? null
    : undefined;

  React.useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const res = await fastapi.get('/tenants/me/features');
        setEnabledFeatures(res.data);
      } catch (e) {
        console.error('Failed to fetch features', e);
        setEnabledFeatures([]); // Fallback to empty if error
      }
    };

    // Only fetch if it's a tenant logged in (has user)
    if (localStorage.getItem('jwtToken')) {
      fetchFeatures();
    }
  }, []);

  return (
    <>
      <DesktopSidebar enabledFeatures={enabledFeatures} channelDefaultBotId={channelDefaultBotId} />
      <MobileSidebar enabledFeatures={enabledFeatures} channelDefaultBotId={channelDefaultBotId} />
    </>
  );
};

export default Sidebar;
